import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, Mail, Trophy, TrendingUp, Target, Award } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { getUserProfile } from '../services/api';
import PushNotificationSettings from '../components/PushNotificationSettings';

const Profiel = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const data = await getUserProfile();
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <User size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Mijn Profiel</h1>
          <p className="text-muted-foreground">Bekijk je statistieken en voorspellingen</p>
        </div>

        {/* User Info Card */}
        <Card className="mb-8 border-2 border-[#FDB913]">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913]">Account Informatie</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-4">
                <div className="bg-[#FDB913] rounded-full p-3">
                  <User className="text-[#1a2332]" size={24} />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Gebruikersnaam</div>
                  <div className="text-lg font-bold text-foreground">{profile?.user?.username || user.username}</div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="bg-[#FDB913] rounded-full p-3">
                  <Mail className="text-[#1a2332]" size={24} />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Email</div>
                  <div className="text-lg font-bold text-foreground">{profile?.user?.email || user.email}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-t-4 border-t-[#FDB913]">
            <CardContent className="pt-6 text-center">
              <div className="bg-[#FDB913] rounded-full p-4 inline-block mb-4">
                <Trophy size={32} className="text-[#1a2332]" />
              </div>
              <div className="text-3xl font-bold text-[#FDB913] mb-1">{profile?.statistics?.total_points || 0}</div>
              <div className="text-muted-foreground font-medium">Totaal Punten</div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-blue-500">
            <CardContent className="pt-6 text-center">
              <div className="bg-blue-500 rounded-full p-4 inline-block mb-4">
                <TrendingUp size={32} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-blue-500 mb-1">{profile?.statistics?.race_predictions || 0}</div>
              <div className="text-muted-foreground font-medium">Race Voorspellingen</div>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-green-500">
            <CardContent className="pt-6 text-center">
              <div className="bg-green-500 rounded-full p-4 inline-block mb-4">
                <Target size={32} className="text-white" />
              </div>
              <div className="text-3xl font-bold text-green-500 mb-1">{profile?.statistics?.qualifying_predictions || 0}</div>
              <div className="text-muted-foreground font-medium">Kwalificatie Voorspellingen</div>
            </CardContent>
          </Card>
        </div>

        {/* Championship Predictions */}
        {profile?.championship_prediction && (
          <Card className="mb-8 border-2 border-[#FDB913]">
            <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
              <CardTitle className="text-[#FDB913] flex items-center space-x-2">
                <Award size={24} />
                <span>Mijn Kampioenschap Voorspellingen</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {profile.championship_prediction.world_champion && (
                  <div className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Wereldkampioen 2026</div>
                    <div className="text-xl font-bold text-foreground">
                      {profile.championship_prediction.world_champion}
                    </div>
                  </div>
                )}

                {profile.championship_prediction.constructor_champion && (
                  <div className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Constructeurs Kampioen 2026</div>
                    <div className="text-xl font-bold text-foreground">
                      {profile.championship_prediction.constructor_champion}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Predictions */}
        <Card className="border-2 border-[#FDB913] mb-8">
          <CardHeader className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50]">
            <CardTitle className="text-[#FDB913]">Recente Voorspellingen</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {!profile?.recent_predictions || profile.recent_predictions.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Je hebt nog geen voorspellingen gedaan</p>
                <Link to="/voorspel">
                  <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]">
                    Maak Je Eerste Voorspelling
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {profile.recent_predictions.map((pred, index) => (
                  <div
                    key={index}
                    className="bg-secondary border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-foreground">
                        {pred.type === 'race' ? 'Race Voorspelling' : 'Kwalificatie Voorspelling'}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Race: {pred.race_id}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(pred.created_at).toLocaleDateString('nl-NL', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Push Notification Settings */}
        <PushNotificationSettings />
      </div>
    </div>
  );
};

export default Profiel;
